import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Card, Badge, Button, Select, ShieldTick, ChevronDown, ChevronUp } from '@opendate-app/setlist';

import { formatCents, formatSeatRange } from '../../../shared/formattingUtils';
import '../styles/chooseResale.css';

const seatPills = (seats) => {
  if (!seats || seats.length === 0) return [];
  const { section, row } = seats[0];
  const pills = [];
  if (section) pills.push(`Section ${section}`);
  if (row) pills.push(`Row ${row}`);
  const seatNums = seats.map((s) => s.seat).filter(Boolean);
  if (seatNums.length === 1) pills.push(`Seat ${formatSeatRange(seatNums)}`);
  else if (seatNums.length > 1) pills.push(`Seats ${formatSeatRange(seatNums)}`);
  return pills;
};

const ChooseResale = ({ resaleListings, resaleOrderItems, resaleQuantityChanged }) => {
  const buyableListings = resaleListings || [];

  const [expanded, setExpanded] = useState(false);

  if (buyableListings.length === 0) {
    return null;
  }

  const totalAvailableCount = buyableListings.reduce(
    (sum, listing) => sum + (listing.available_count || 0),
    0
  );

  return (
    <Card padding="none" className="resale-checkout" data-testid="resale-section">
      <div
        className="resale-checkout-header"
        onClick={() => setExpanded((prev) => !prev)}
        role="button"
        aria-expanded={expanded}
        aria-controls="resale-section-body"
      >
        <span className="resale-checkout-title">Verified Resell</span>
        <span className="resale-checkout-count">
          {totalAvailableCount} {buyableListings.some((listing) => listing.add_on)
            ? "available"
            : totalAvailableCount === 1 ? "Ticket" : "Tickets"}
          {expanded ? (
            <ChevronUp width={16} height={16} aria-hidden="true" />
          ) : (
            <ChevronDown width={16} height={16} aria-hidden="true" />
          )}
        </span>
      </div>
      {expanded ? (
        <div id="resale-section-body" className="resale-checkout-body">
          {buyableListings.map((listing) => {
            const availableCount = listing.available_count || 0;
            const selection = (resaleOrderItems || []).find((s) => s.listingId === listing.id);
            const selectedQuantity = selection ? selection.quantity : 0;

            const seats = listing.available_seats || [];
            const isSeated = seats.length > 0;
            const pills = isSeated ? seatPills(seats) : [];

            const changeQuantity = (quantity) => {
              resaleQuantityChanged(
                listing.id,
                quantity,
                listing.ticket_type_name,
                listing.list_price_cents,
                listing.buyer_price_before_tax_per_ticket_cents
              );
            };

            return (
              <div className="resale-checkout-listing" key={listing.id} data-testid={`resale-listing-${listing.id}`}>
                <div className="resale-checkout-listing-info">
                  {isSeated ? (
                    <div className="resale-checkout-seats" data-testid={`resale-listing-${listing.id}-seats`}>
                      {pills.map((pill) => (
                        <Badge key={pill} color="gray">{pill}</Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="resale-checkout-listing-name">{listing.ticket_type_name}</p>
                  )}
                  {isSeated ? (
                    <p className="resale-checkout-seat">{listing.ticket_type_name}</p>
                  ) : (
                    <p className="resale-checkout-listing-avail">{availableCount} available</p>
                  )}
                  {listing.entry_label && (
                    <p className="resale-checkout-listing-avail" data-testid={`resale-listing-${listing.id}-entry-label`}>
                      {listing.entry_label}
                    </p>
                  )}
                  <Badge color="success">
                    <ShieldTick width={12} height={12} aria-hidden="true" /> Verified Resell
                  </Badge>
                </div>
                <div className="resale-checkout-listing-buy">
                  <div className="resale-checkout-price">
                    <div className="resale-checkout-price-amount">
                      {formatCents(listing.buyer_price_before_tax_per_ticket_cents)}
                    </div>
                    <div className="resale-checkout-price-note">{listing.add_on ? "each" : "per ticket"}, incl. fees{listing.buyer_total_per_ticket_cents > listing.buyer_price_before_tax_per_ticket_cents ? ", plus tax" : ""}</div>
                  </div>
                  {isSeated ? (
                    <Button
                      variant={selectedQuantity > 0 ? "secondary" : "primary"}
                      data-testid={`resale-listing-${listing.id}-toggle`}
                      aria-pressed={selectedQuantity > 0}
                      onClick={() => changeQuantity(selectedQuantity > 0 ? 0 : availableCount)}
                    >
                      {selectedQuantity > 0 ? "Remove" : "Add"}
                    </Button>
                  ) : (
                    <Select
                      aria-label="Resale Ticket Quantity"
                      data-testid={`resale-listing-${listing.id}-quantity`}
                      width={88}
                      value={selectedQuantity}
                      onChange={(value) => changeQuantity(parseInt(value, 10))}
                      options={Array.from({ length: availableCount + 1 }, (_, quantity) => ({
                        value: quantity,
                        label: String(quantity)
                      }))}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : null}
    </Card>
  );
};

ChooseResale.propTypes = {
  resaleListings: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    available_count: PropTypes.number,
    ticket_type_name: PropTypes.string,
    entry_label: PropTypes.string,
    add_on: PropTypes.bool,
    list_price_cents: PropTypes.number.isRequired,
    buyer_total_per_ticket_cents: PropTypes.number,
    buyer_price_before_tax_per_ticket_cents: PropTypes.number,
    available_seats: PropTypes.arrayOf(PropTypes.shape({
      section: PropTypes.string,
      row: PropTypes.string,
      seat: PropTypes.string
    }))
  })),
  resaleOrderItems: PropTypes.arrayOf(PropTypes.shape({
    listingId: PropTypes.number.isRequired,
    quantity: PropTypes.number.isRequired,
    ticketTypeName: PropTypes.string,
    listPriceCents: PropTypes.number,
    buyerTotalPerTicketCents: PropTypes.number
  })),
  resaleQuantityChanged: PropTypes.func.isRequired
};

export default ChooseResale;
